import { usePage } from '@inertiajs/react';

type Translations = { [key: string]: TranslationValue };
type TranslationValue = string | { [key: string]: TranslationValue } | TranslationValue[];

export const useI18n = () => {
  const {
    props: { locale, currency, translations },
  } = usePage();

  const cheapestPrice = Math.round(49 * currency.multiplier);

  function t(key: string, replacements: Record<string, string | number> = {}): string {
    const value = getTranslation(key);

    if (typeof value === 'string') {
      return replacePlaceholders(value, replacements);
    }

    return key;
  }

  function tArr<T = string>(key: string, replacements: Record<string, string | number> = {}): T[] {
    const value = getTranslation(key);

    if (typeof value === 'object') {
      return value as T[];
    }

    return [key] as T[];
  }

  function applyCase(placeholder: string, value: string): string {
    if (placeholder === placeholder.toUpperCase()) {
      return value.toUpperCase();
    } else if (placeholder[0] === placeholder[0].toUpperCase()) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
  }

  function replacePlaceholders(text: string, replacements: Record<string, string | number>): string {
    const normalized: Record<string, string> = {};
    for (const key in replacements) {
      normalized[key.toLowerCase()] = String(replacements[key]);
    }
    return text.replace(/:([a-zA-Z_]+)/g, (match, p1) => {
      const rep = normalized[p1.toLowerCase()];
      return rep !== undefined ? applyCase(p1, rep) : match;
    });
  }

  function getTranslation(key: string): TranslationValue | undefined {
    const keys = key.split('.');
    let result: TranslationValue | Translations = translations;

    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        // @ts-expect-error dunno
        result = result[key] as TranslationValue | Translations;
      } else {
        return undefined;
      }
    }

    return result;
  }

  function formatNumber(number: number, asCurrency = false, maximumFractionDigits = 2, trailingZeroDisplay = false): string {
    let formatted: string;

    try {
      formatted = new Intl.NumberFormat(locale, {
        currency: currency.code,
        style: asCurrency ? 'currency' : 'decimal',
        // maximumSignificantDigits: 5,
        minimumFractionDigits: maximumFractionDigits,
        maximumFractionDigits,
        trailingZeroDisplay: trailingZeroDisplay ? 'auto' : 'stripIfInteger',
      }).format(number);
    } catch (_e) {
      formatted = '-';
    }

    return formatted;
  }

  function formatNumberShort(number: number): string {
    if (number < 1000) {
      return formatNumber(number);
    }

    if (number < 10000) {
      return `${Math.round(number / 100) / 10}K`;
    }

    if (number < 1000000) {
      return `${Math.round(number / 1000)}K`;
    }

    return `${Math.round(number / 1000000)}M`;
  }

  function formatDate(dateStr: string): string {
    return Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(dateStr));
  }

  return {
    t,
    tArr,
    formatNumber,
    formatNumberShort,
    formatDate,
    locale,
    currency,
    translations,
    cheapestPrice,
  };
};
